import type {
	SnipeTransferPlayerSuggestionOutput,
	getPlayerDatabaseInput,
} from "@/api/custom_types";
import { ToolbarSwitcher } from "@/components/toolbar-switcher";
import { useApiError } from "@/hooks";
import { useFacets } from "@/hooks/useFacetFilters";
import { usePersistedServerTableState } from "@/hooks/usePersistedServerTableState";
import { useTranslation } from "@/hooks/useTranslation";
import {
	PLAYER_DATABASE_FACET_SOURCE,
	TRANSFER_PLAYER_SUGGESTIONS_FACET_SOURCE,
	useCreateTransferPlayers,
	useGetPlayerDatabase,
	useGetTransferPlayerSuggestions,
	useRefreshPlayerPrices,
} from "@/services";
import { cleanQuery, omit } from "@/utils/queryClient";
import { formatNumber } from "@/utils/tools";
import {
	ActionItem,
	ActionMenu,
	Button,
	Card,
	CardContent,
	DataTable,
	Typography,
	toast,
	useDataTableRowSelection,
} from "@st-marketplace/ui";
import type { ColumnDef } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { RefreshPriceButton } from "./RefreshPriceButton";
import {
	type FilterFormData,
	STAdminTransferPlayersFilter,
} from "./STAdminTransferPlayersFilter";

type PoolQuery = NonNullable<getPlayerDatabaseInput["query"]>;

const PlayerPoolRowAction = ({ resourceId }: { resourceId: number }) => {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const refreshPrices = useRefreshPlayerPrices();

	return (
		// biome-ignore lint/a11y/useKeyWithClickEvents: stopPropagation-only container; inner menu remains keyboard-accessible
		<div onClick={(event) => event.stopPropagation()}>
			<ActionMenu open={open} onOpenChange={setOpen}>
				<ActionItem
					leadingIconName="price-tag-3-line"
					loading={refreshPrices.isPending}
					onClick={() =>
						refreshPrices.mutate(
							{ body: { resource_ids: [resourceId] } },
							{ onSettled: () => setOpen(false) },
						)
					}
				>
					{t("admin.transferPlayers.refreshPrice")}
				</ActionItem>
			</ActionMenu>
		</div>
	);
};

/**
 * Cards that can be added to the transfer players:
 * - `suggestions`: the backend's picks (expensive, tradeable, not added yet)
 * - `database`: every card, optionally without the ones already added
 */
const PlayerPoolTable = ({ pool }: { pool: "suggestions" | "database" }) => {
	const { t } = useTranslation();
	const { getErrorInterface } = useApiError();
	const isDatabase = pool === "database";

	const server = usePersistedServerTableState(
		isDatabase ? "st-admin-player-database" : "st-admin-player-suggestions",
	);
	const rowSelection =
		useDataTableRowSelection<SnipeTransferPlayerSuggestionOutput>(true);
	const [filters, setFilters] = useState<FilterFormData>({
		activationState: [],
		rarityName: [],
		clubName: [],
		leagueName: [],
		nationName: [],
	});

	const handleSearch = useCallback(
		(values: FilterFormData) => {
			setFilters(values);
			server.onPageChange(0);
		},
		[server],
	);

	const scope = isDatabase
		? { exclude_transfer_players: filters.transferPlayers === "exclude" }
		: undefined;
	const listQuery = cleanQuery<PoolQuery>({
		page: server.pageIndex + 1,
		page_size: server.pageSize,
		player_nick_name: filters.playerNickname,
		club_names: filters.clubName,
		rarity_names: filters.rarityName,
		league_names: filters.leagueName,
		nation_names: filters.nationName,
		...scope,
	});

	const suggestionsQuery = useGetTransferPlayerSuggestions(
		{ query: listQuery },
		{ enabled: !isDatabase },
	);
	const databaseQuery = useGetPlayerDatabase(
		{ query: listQuery },
		{ enabled: isDatabase },
	);
	const {
		data: players,
		isLoading,
		isFetching,
		error,
		refetch,
	} = isDatabase ? databaseQuery : suggestionsQuery;

	const facetsCounts = useFacets({
		source: isDatabase
			? PLAYER_DATABASE_FACET_SOURCE
			: TRANSFER_PLAYER_SUGGESTIONS_FACET_SOURCE,
		scope,
		filters: omit(listQuery, [
			"page",
			"page_size",
			"exclude_transfer_players",
		] as const),
	});

	const createTransferPlayers = useCreateTransferPlayers();

	const formatPriceValue = useCallback(
		(value: number | null | undefined) =>
			value == null ? "-" : formatNumber(value, "count"),
		[],
	);

	const selectedResourceIds = rowSelection.selectedIds
		.map((rowId) => Number(rowId))
		.filter((resourceId) => Number.isFinite(resourceId));

	const handleAddSelected = () => {
		if (selectedResourceIds.length === 0) {
			toast.error(t("admin.transferPlayers.suggest.emptySelection"));
			return;
		}

		createTransferPlayers.mutate(
			{
				body: {
					resources: selectedResourceIds.map((resource_id) => ({
						resource_id,
						activation_state: "ACTIVE",
					})),
				},
			},
			{
				onSuccess: () => {
					rowSelection.clear();
				},
			},
		);
	};

	const rows = players?.results ?? [];
	const rowCount = players?.count ?? rows.length;

	const columns = useMemo<ColumnDef<SnipeTransferPlayerSuggestionOutput>[]>(
		() => [
			{
				id: "nickname",
				header: t("admin.transferPlayers.table.nickname"),
				enableSorting: false,
				cell: ({ row }) =>
					`${row.original.player_metadata.nick_name} ${row.original.player_metadata.rating}`,
			},
			{
				id: "last_price",
				header: t("admin.transferPlayers.table.lastPrice"),
				enableSorting: false,
				cell: ({ row }) =>
					formatPriceValue(row.original.player_metadata.last_price),
			},
			{
				id: "price_range",
				header: t("admin.transferPlayers.table.priceRange"),
				enableSorting: false,
				cell: ({ row }) =>
					`${formatPriceValue(
						row.original.player_metadata.min_price_range,
					)} - ${formatPriceValue(
						row.original.player_metadata.max_price_range,
					)}`,
			},
			{
				id: "picture",
				header: t("admin.transferPlayers.table.picture"),
				enableSorting: false,
				cell: ({ row }) => {
					const src = row.original.player_metadata.picture;
					if (!src) return "-";
					return (
						<div className="w-18">
							<img
								src={src}
								alt={row.original.player_metadata.nick_name ?? "player"}
								loading="lazy"
								decoding="async"
								referrerPolicy="no-referrer"
								className="h-24 w-full object-cover"
							/>
						</div>
					);
				},
			},
			{
				id: "rarity_name",
				header: t("admin.transferPlayers.table.rarity"),
				enableSorting: false,
				cell: ({ row }) => row.original.player_metadata.rarity_name,
			},
			{
				id: "resource_id",
				header: t("admin.transferPlayers.table.resourceId"),
				enableSorting: false,
				cell: ({ row }) => row.original.resource_id,
			},
			{
				id: "actions",
				header: "",
				enableSorting: false,
				cell: ({ row }) => (
					<PlayerPoolRowAction resourceId={row.original.resource_id} />
				),
			},
		],
		[t, formatPriceValue],
	);

	return (
		<div className="flex flex-col gap-6">
			<ToolbarSwitcher
				hasSelection={rowSelection.selectedIds.length > 0}
				filterComponent={
					<STAdminTransferPlayersFilter
						defaultValues={filters}
						facetCounts={facetsCounts}
						onSearch={handleSearch}
						showTransferPlayersFilter={isDatabase}
					/>
				}
				actionComponent={
					<Card>
						<CardContent className="flex flex-wrap items-center justify-between gap-3">
							<Typography variant="title-sm">
								{t("admin.transferPlayers.selectedPlayers", {
									value: rowSelection.selectedIds.length,
								})}
							</Typography>
							<div className="flex flex-wrap gap-2">
								<RefreshPriceButton
									resourceIds={selectedResourceIds}
									onSuccess={rowSelection.clear}
								/>
								<Button
									type="button"
									color="primary"
									loading={createTransferPlayers.isPending}
									disabled={createTransferPlayers.isPending}
									onClick={handleAddSelected}
								>
									{t("admin.transferPlayers.suggest.addSelected")}
								</Button>
							</div>
						</CardContent>
					</Card>
				}
			/>

			<DataTable
				columns={columns}
				data={rows}
				totalItems={rowCount}
				server={server}
				isLoading={isLoading}
				isFetching={isFetching}
				error={error ? getErrorInterface(error) : null}
				onRetry={refetch}
				getRowId={(row) => String(row.resource_id)}
				rowSelection={rowSelection}
			/>
		</div>
	);
};

export default PlayerPoolTable;
